import type React from 'react';
import { useEffect, useState } from 'react';
import {
  Alert,
  Anchor,
  Avatar,
  Box,
  Button,
  Card,
  FieldSet,
  FormField,
  FormHelp,
  Heading,
  Icon,
  Input,
  Label,
  LabeledControl,
  Legend,
  MediaObject,
  Modal,
  Paragraph,
  Radio,
  Select,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { IconUserGroupFilled } from '@zillow/constellation-icons';
import { Controller, useFormContext } from 'react-hook-form';
import { FIELD_IDS, getIsMobilityImpairedVisible } from '../../../../constants/wizard';
import type { Answers, RentalListing, SentCoapplicantInvitation } from '../../../../graphql/queries';
import {
  GetRentalApplicationDocument,
  InviteApplicantDocument,
  RemoveApplicantDocument,
} from '../../../../graphql/queries';
import { useApolloClient, useMutation } from '@apollo/client';
import { BlockLoadingMask, FullHeightLoadingMask } from '../../../../styles/css-components';
import { validate } from 'email-validator';
import {
  CO_APPLICANT_FAQ_URL,
  PORTLAND_APPLICANT_RIGHTS_URL,
  PORTLAND_MODIFICATION_ACCOMMODATION_URL,
} from '../../../../constants/urls';
import { logError } from '@zg-rentals/log-error';
import { useApplicationContext } from '../../../../contexts/ApplicationContext';
import { get } from 'lodash-es';
import { isValidNonNull } from '../../../../utils/validation';

type Props = {
  applicationId: string;
  answers: Answers;
  isPrimaryApplicant: boolean;
  rentalListing?: RentalListing | null;
};

export function OccupantsStep({ applicationId, answers, isPrimaryApplicant, rentalListing }: Props) {
  const {
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useFormContext();

  useEffect(() => {
    if (!getValues(FIELD_IDS.OCCUPANTS)) {
      setValue(FIELD_IDS.OCCUPANTS, 1);
    }
  }, []);

  const numOccupants = watch(FIELD_IDS.OCCUPANTS);
  const applicants = (answers.coapplicants as Array<SentCoapplicantInvitation>) || [];
  const isMobilityImpairedVisible = rentalListing && getIsMobilityImpairedVisible(rentalListing);
  const mobilityError = get(errors, FIELD_IDS.MOBILITY_IMPAIRED);

  return (
    <>
      <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default', fontFamily: 'heading-alt' }}>
        Occupants
      </Heading>
      <Controller
        name={FIELD_IDS.OCCUPANTS}
        rules={{ required: 'A number of occupants is required' }}
        render={({ field: { onChange, value, onBlur } }) => (
          <FormField
            label={<Label>How many people will be living in this home, including children?</Label>}
            control={
              <Select
                value={value || ''}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(Number(e.target.value))}
                onBlur={onBlur}
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
                <option value={5}>5</option>
                <option value={6}>6</option>
                <option value={7}>7</option>
                <option value={8}>8</option>
                <option value={9}>9</option>
                <option value={10}>10</option>
                <option value={11}>11</option>
                <option value={12}>12</option>
              </Select>
            }
            description={
              errors.occupants?.message ? <FormHelp>{String(errors.occupants.message)}</FormHelp> : undefined
            }
            error={!!errors.occupants}
          />
        )}
      />
      {isPrimaryApplicant && numOccupants > 1 && (
        <ApplicationGroup applicationId={applicationId} numOccupants={numOccupants} applicants={applicants} />
      )}

      {isMobilityImpairedVisible && (
        <>
          <Controller
            name={FIELD_IDS.MOBILITY_IMPAIRED}
            rules={{ validate: isValidNonNull }}
            render={({ field: { onChange, value, onBlur } }) => (
              <FieldSet
                legend={
                  <Legend>
                    Is someone in your household mobility-impaired, or does anyone otherwise require disability
                    accommodations?
                  </Legend>
                }
                description={mobilityError?.message ? <FormHelp>{String(mobilityError.message)}</FormHelp> : undefined}
                error={!!mobilityError}
                css={{ marginBottom: 'tighter' }}
                onBlur={onBlur}
              >
                <LabeledControl
                  onChange={() => onChange({ id: 'mobilityImpaired', value: true })}
                  label={<Label>Yes</Label>}
                  control={<Radio name={FIELD_IDS.MOBILITY_IMPAIRED} defaultChecked={value?.value === true} />}
                />
                <LabeledControl
                  onChange={() => onChange({ id: 'mobilityImpaired', value: false })}
                  label={<Label>No</Label>}
                  control={<Radio name={FIELD_IDS.MOBILITY_IMPAIRED} defaultChecked={value?.value === false} />}
                />
              </FieldSet>
            )}
          />
          <Paragraph textStyle="body-xs">
            Under Portland law, a landlord cannot deny a tenant based on disability status, and tenants can modify their
            accommodation request during the application process. Learn more about{' '}
            <Anchor href={PORTLAND_MODIFICATION_ACCOMMODATION_URL} target="_blank" rel="noopener noreferrer">
              modifications or accommodations
            </Anchor>{' '}
            and{' '}
            <Anchor href={PORTLAND_APPLICANT_RIGHTS_URL} target="_blank" rel="noopener noreferrer">
              applicant rights
            </Anchor>
            .
          </Paragraph>
        </>
      )}
    </>
  );
}

export function ApplicationGroup({
  applicationId,
  numOccupants,
  applicants,
}: {
  applicationId: string;
  numOccupants: number;
  applicants: Array<SentCoapplicantInvitation>;
}) {
  const [isAdding, setIsAdding] = useState(false);
  const canAddGuest = numOccupants - applicants.length - 1 > 0;

  return (
    <>
      <Heading
        level={3}
        textStyle="heading-xs"
        css={{ marginTop: 'default', marginBottom: 'default', fontFamily: 'heading-alt' }}
      >
        Co-applicants
      </Heading>
      <Alert
        actionButton={
          <TextButton asChild>
            <a href={CO_APPLICANT_FAQ_URL} target="_blank" rel="noopener noreferrer">
              See more information
            </a>
          </TextButton>
        }
      >
        Any household member 18 years or older is considered a co-applicant. They’ll need to create their own Zillow
        account and pay their own application fee. Provide their name and contact information, and we’ll email them an
        invitation to apply.
      </Alert>
      {applicants.map((applicant) => (
        <ApplicationApplicant key={applicant.id} applicationId={applicationId} applicant={applicant} />
      ))}
      {canAddGuest && (
        <>
          <Button fluid css={{ marginTop: 'default' }} onClick={() => setIsAdding(true)} type="button">
            Add co-applicant
          </Button>
          {isAdding && <ApplicationAddGuest applicationId={applicationId} onClose={() => setIsAdding(false)} />}
        </>
      )}
      <Paragraph textStyle="body-xs" css={{ marginTop: 'tighter' }}>
        When you’ve added all co-applicants, continue filling out this application{' '}
        <strong>with your information only</strong>.
      </Paragraph>
    </>
  );
}

function ApplicationApplicant({
  applicationId,
  applicant,
}: {
  applicationId: string;
  applicant: SentCoapplicantInvitation;
}) {
  const [isRemoving, setIsRemoving] = useState(false);
  const { enqueueToast } = useToast();
  const [removeApplicantMutation] = useMutation(RemoveApplicantDocument);
  const apolloClient = useApolloClient();
  const invitationId = applicant.id || '';

  async function onRemoveApplicant() {
    setIsRemoving(true);
    try {
      await removeApplicantMutation({
        variables: {
          applicationId,
          invitationId,
        },
      });

      await apolloClient.refetchQueries({ include: [GetRentalApplicationDocument] });
    } catch (err) {
      logError({ error: err, context: 'ApplicationApplicant - removeApplicantMutation error' });
      enqueueToast(<Toast tone="critical">We couldn’t remove the applicant. Try again.</Toast>);
    } finally {
      setIsRemoving(false);
    }
  }

  return (
    <BlockLoadingMask loading={isRemoving}>
      <Box
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 'default',
          marginBottom: 'default',
          alignItems: 'center',
        }}
      >
        <MediaObject
          media={<Avatar fullName={`${applicant.firstName}${applicant.lastName}`} tone="accent-1" />}
          css={{ alignItems: 'center' }}
        >
          <Paragraph textStyle="body-sm">
            {applicant.firstName} {applicant.lastName}
          </Paragraph>
          <Paragraph textStyle="body-xs" css={{ color: 'text.subtle' }}>
            {String(applicant.email)}
          </Paragraph>
        </MediaObject>
        <TextButton onClick={onRemoveApplicant}>Remove</TextButton>
      </Box>
    </BlockLoadingMask>
  );
}

function ApplicationAddGuest({ applicationId, onClose }: { applicationId: string; onClose: () => void }) {
  const { enqueueToast } = useToast();
  const { application } = useApplicationContext();

  const ownEmail = application.answers?.email as string;
  const coapplicants = application.answers?.coapplicants;

  const coapplicantsSet = new Set<string>();
  if (coapplicants && coapplicants.length > 0) {
    coapplicants.forEach(
      (coapplicant) => coapplicant?.email && coapplicantsSet.add((coapplicant.email as string).toLowerCase()),
    );
  }

  const [isSaving, setIsSaving] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  const [inviteApplicantMutation] = useMutation(InviteApplicantDocument);
  const apolloClient = useApolloClient();

  async function onAddSave() {
    setIsSaving(true);
    try {
      await inviteApplicantMutation({
        variables: {
          applicationId,
          applicant: {
            firstName,
            lastName,
            email,
          },
        },
      });

      await apolloClient.refetchQueries({ include: [GetRentalApplicationDocument] });
      onClose();
    } catch (err) {
      logError({ error: err, context: 'ApplicationAddGuest - inviteApplicantMutation error' });
      enqueueToast(<Toast tone="critical">We couldn’t invite the applicant. Try again.</Toast>);
    } finally {
      setIsSaving(false);
    }
  }

  const isEmailOwnEmail = ownEmail.toLowerCase() === email.toLowerCase();
  const isExistingCoapplicant = coapplicantsSet.has(email.toLowerCase());

  const isValid =
    firstName?.trim().length > 0 &&
    lastName?.trim().length > 0 &&
    email?.trim().length > 0 &&
    validate(email) &&
    !isEmailOwnEmail &&
    !isExistingCoapplicant;

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      header={<Heading level={2}>Invite co-applicant</Heading>}
      body={
        <FullHeightLoadingMask loading={isSaving}>
          <FormField
            label={<Label>First name</Label>}
            control={
              <Input
                maxLength={32}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.currentTarget.value)}
              />
            }
            css={{ marginBottom: 'default' }}
          />
          <FormField
            label={<Label>Last name</Label>}
            control={
              <Input
                maxLength={32}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.currentTarget.value)}
              />
            }
            css={{ marginBottom: 'default' }}
          />
          <FormField
            label={<Label>Email</Label>}
            control={
              <Input
                maxLength={100}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.currentTarget.value)}
              />
            }
            description={
              (isEmailOwnEmail || isExistingCoapplicant) && (
                <FormHelp>
                  {isEmailOwnEmail
                    ? `You cannot add yourself as a co-applicant`
                    : `You already invited a co-applicant with this email address`}
                </FormHelp>
              )
            }
            error={isEmailOwnEmail || isExistingCoapplicant}
          />
        </FullHeightLoadingMask>
      }
      footer={
        <Button tone="brand" emphasis="filled" onClick={onAddSave} disabled={!isValid || isSaving}>
          Invite
        </Button>
      }
    />
  );
}

export function OccupantsStepTip() {
  return (
    <Card
      elevated={false}
      tone="neutral"
      body={
        <>
          <Icon size="xl" css={{ marginBottom: 'default' }}>
            <IconUserGroupFilled />
          </Icon>
          <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default', fontFamily: 'heading-alt' }}>
            Your household
          </Heading>
          <Paragraph>Rental managers need to know about any other people or pets you’ll be living with.</Paragraph>
        </>
      }
    />
  );
}
